import React, { useState } from 'react';

import Dropdown from 'common/components/Dropdown';
import { I18n } from 'common/i18n';
import SingleColumnSelector, { DropdownOption } from './SingleColumnSelector';
import filter from 'lodash/filter';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import merge from 'lodash/merge';
import { ForgeIconButton, ForgeIcon, ForgeButton } from '@tylertech/forge-react';

const scope = 'shared.visualizations.panes.data.fields';

// Renders multiple rows of column dropdown and aggregation dropdown for selection.
// On add new, it renders only a column selector in a row and is called inProgressColumn selector.
// After selecting the column, the aggregation selector is shown defaulting to the first value as
// selected and onNew callback is called.
const ColumnAndAggregationSelector: React.FC<ColumnAndAggregationSelectorProps> = ({
  aggregations,
  columns,
  disableInProgressColumnSelector = false,
  isFlyoutSeries = true,
  onAdd,
  onDelete,
  onUpdate,
  shouldRenderAddLink,
  shouldRenderDeleteLink,
  showAlreadySelectedColumns,
  showNewInProgressColumnAndAggregateSelector: initialShowNewInProgress,
  values,
}) => {
  const [showNewInProgressColumnAndAggregateSelector, setShowNewInProgressColumnAndAggregateSelector] =
    useState(initialShowNewInProgress);

  const handleOnClickAddLink = () => {
    setShowNewInProgressColumnAndAggregateSelector(true);
  };

  const handleOnSelectAggregation = (
    index: number,
    aggregationValue: string | null,
    columnAndAggregateValue: ColumnAndAggregation,
  ) => {
    const seriesIndex = get(columnAndAggregateValue, 'seriesIndex', index);

    onUpdate(index, {
      column: columnAndAggregateValue.column,
      seriesIndex,
      aggregation: aggregationValue,
    });
  };

  const handleOnSelectColumn = (
    index: number,
    columnValue: string | null,
    columnAndAggregateValue: ColumnAndAggregation | null,
  ) => {
    if (isNil(columnAndAggregateValue)) {
      setShowNewInProgressColumnAndAggregateSelector(false);
      // Completed a in progress columnAndAggregateSelection
      onAdd(columnValue);
    } else {
      const seriesIndex = get(columnAndAggregateValue, 'seriesIndex', index);

      onUpdate(index, {
        column: columnValue,
        seriesIndex,
        aggregation: columnAndAggregateValue.aggregation,
      });
    }
  };

  const renderAddLink = () => {
    const title = isFlyoutSeries
      ? I18n.t('flyout_measure.add_flyout_value', { scope })
      : I18n.t('measure.add_measure', { scope });
    return (
      <div className="add-link-container">
        <ForgeButton
          id={'add-measure-link'}
          disabled={showNewInProgressColumnAndAggregateSelector}
          onClick={handleOnClickAddLink}
          dense
        >
          <ForgeIcon name={'add'} dense></ForgeIcon>
          <span>{title}</span>
        </ForgeButton>
      </div>
    );
  };

  const renderAggregationSelector = (columnAndAggregateValue: ColumnAndAggregation, index: number) => {
    // Display no aggregation dropdown when no column name is selected or when
    // displayAggregation is false.
    const column = get(columnAndAggregateValue, 'column');
    const displayAggregation = get(columnAndAggregateValue, 'displayAggregation', true);

    if (isNil(column) || !displayAggregation) {
      return null;
    }

    const options = [
      ...aggregations.map((aggregation) => ({ title: aggregation.title, value: aggregation.type })),
      { title: I18n.translate('shared.visualizations.aggregations.none'), value: null },
    ];

    const aggregationAttributes = {
      disabled: disableInProgressColumnSelector,
      id: `aggregation-selection-${index}`,
      label: I18n.t('measure.aggregation_label', { scope, number: index + 1 }),
      options,
      value: columnAndAggregateValue.aggregation,
      onSelection: (aggregation: { title: string; value: string | null }) => {
        handleOnSelectAggregation(index, aggregation.value, columnAndAggregateValue);
      },
    };

    return (
      <div className="secondary-dropdown-container">
        <Dropdown {...aggregationAttributes} />
      </div>
    );
  };

  const renderColumnAndAggregationSelector = (columnAndAggregateValue: ColumnAndAggregation, key: number) => {
    return (
      <li className="list-item" key={key}>
        {renderColumnSelector(columnAndAggregateValue, key)}
        {renderAggregationSelector(columnAndAggregateValue, key)}
        {renderDeleteLink(columnAndAggregateValue, key)}
      </li>
    );
  };

  const renderColumnSelector = (columnAndAggregateValue: ColumnAndAggregation | null, index: number) => {
    const columnValue = get(columnAndAggregateValue, 'column', null);
    const unavailableColumns = showAlreadySelectedColumns ? [] : map(values, 'column');
    const withCurrentColumnOptions = filter(columns, (column) => {
      return !includes(unavailableColumns, column.value) || columnValue === column.value;
    });

    let columnAttributes = {
      disabled: disableInProgressColumnSelector,
      id: `column-selection-${index}`,
      shouldRenderDeleteColumnLink: false,
      label: I18n.t('measure.column_label', { scope, number: index + 1 }),
      options: withCurrentColumnOptions,
      placeholder: I18n.t('measure.select_column', { scope }),
      onSelection: (column: DropdownOption) => {
        handleOnSelectColumn(index, column.value, columnAndAggregateValue);
      },
    };

    if (!isNil(columnAndAggregateValue)) {
      columnAttributes = merge({ value: columnValue }, columnAttributes);
    }

    return (
      <div className="primary-dropdown-container">
        <SingleColumnSelector {...columnAttributes} />
      </div>
    );
  };

  const renderInProgressColumnSelector = () => {
    return (
      <li className="list-item in-progress-column" key={values.length}>
        {renderColumnSelector(null, values.length)}
      </li>
    );
  };

  const renderDeleteLink = (columnAndAggregateValue: ColumnAndAggregation, index: number) => {
    const seriesIndex = isFlyoutSeries ? index : columnAndAggregateValue.seriesIndex;
    if (!shouldRenderDeleteLink && !isFlyoutSeries) {
      return null;
    }

    return (
      <div className="delete-link-container">
        <ForgeIconButton
          ariaLabel={I18n.t('measure.delete_label', { scope, number: index + 1 })}
          className="delete-selected-column-button delete-link"
          dense={true}
          id={`column-delete-link-${index}`}
          onClick={() => onDelete(seriesIndex)}
          slot="end"
        >
          <ForgeIcon name="clear" />
        </ForgeIconButton>
      </div>
    );
  };

  const columnAndAggregationSelectors = map(values, renderColumnAndAggregationSelector);
  const addLink = shouldRenderAddLink ? renderAddLink() : null;
  return (
    <div>
      <ul className="dropdowns-list column-aggregation-selector">
        {columnAndAggregationSelectors}
        {showNewInProgressColumnAndAggregateSelector && renderInProgressColumnSelector()}
      </ul>
      {addLink}
    </div>
  );
};

export interface ColumnAndAggregationSelectorProps {
  aggregations: Aggregation[];
  columns: DropdownOption[];
  disableInProgressColumnSelector?: boolean;
  isFlyoutSeries?: boolean;
  onAdd: (columnName: string | null) => void;
  onDelete: (index: number) => void;
  onUpdate: (index: number, columnAndAggregation: ColumnAndAggregation) => void;
  shouldRenderAddLink: boolean;
  shouldRenderDeleteLink: boolean;
  showAlreadySelectedColumns: boolean;
  showNewInProgressColumnAndAggregateSelector: boolean;
  values: ColumnAndAggregation[];
}

interface Aggregation {
  title: string;
  type: string;
}

export interface ColumnAndAggregation {
  /** Used to render the aggregation column. */
  aggregation: string | null;
  /** Used to render the column. */
  column: string | null;
  /** Used to determine if the aggregation dropdown should appear. */
  displayAggregation?: boolean;
  /** Series index for tracking purposes. */
  seriesIndex: number;
}

export default ColumnAndAggregationSelector;
